import React, {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";

import { Link } from "react-router-dom";
import { GlobalSet } from "../../global/GlobalProvider";
import axios from "axios";
import { useTranslation } from "react-i18next";


/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
    "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

const PER_PAGE = 10;


/* =========================================================
   HELPERS
========================================================= */

const formatDate = (value) => {

    if (!value) {
        return "—";
    }

    try {

        const normalized =
            String(value).replace(" ", "T");

        const date =
            new Date(normalized);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value;
        }

        return date.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );

    } catch (error) {

        return value;
    }
};


const formatTime = (value) => {

    if (!value) {
        return "";
    }

    try {

        const normalized =
            String(value).replace(" ", "T");

        const date =
            new Date(normalized);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return "";
        }

        return date.toLocaleTimeString(
            "en-US",
            {
                hour: "2-digit",
                minute: "2-digit"
            }
        );

    } catch (error) {

        return "";
    }
};


/* =========================================================
   COMPONENT
========================================================= */

const BasicDatatable = () => {

    const { t } =
        useTranslation();

    const { CurrencySymbol } =
        GlobalSet();


    /* =====================================================
       STATE
    ===================================================== */

    const [income, setIncome] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [currentPage, setCurrentPage] =
        useState(1);


    /* =====================================================
       LOAD TICKETS
    ===================================================== */

    const fetchTickets =
        async () => {

            const selectedCurrency =
                localStorage.getItem(
                    "selectedCurrency"
                );

            const authToken =
                localStorage.getItem(
                    "auth_token"
                );


            if (!authToken) {

                setLoading(false);

                setError(
                    "Your login session was not found. Please login again."
                );

                return;
            }


            const data = {

                key_s:
                    API_KEY,

                currency:
                    selectedCurrency

            };


            try {

                setLoading(true);

                setError("");


                const response =
                    await axios.post(
                        "/ticket",
                        data
                    );


                const rows =
                    response?.data?.data;


                setIncome(
                    Array.isArray(rows)
                        ? rows
                        : []
                );


            } catch (error) {

                console.error(
                    "Ticket Load Error:",
                    error
                );


                setIncome([]);


                setError(
                    error?.response?.data
                        ?.message ||
                    "Unable to load support tickets. Please try again."
                );


            } finally {

                setLoading(false);
            }
        };


    useEffect(() => {

        fetchTickets();

    }, []);


    /* =====================================================
       SEARCH
    ===================================================== */

    const filteredTickets =
        useMemo(() => {

            const keyword =
                search
                    .trim()
                    .toLowerCase();


            if (!keyword) {

                return income;
            }


            return income.filter(
                (item) => {

                    const searchable = [

                        item.ticket,

                        item.subject,

                        item.status,

                        item.created_at

                    ]
                        .filter(
                            value =>
                                value !== null &&
                                value !== undefined
                        )
                        .join(" ")
                        .toLowerCase();


                    return searchable.includes(
                        keyword
                    );
                }
            );

        }, [
            income,
            search
        ]);


    useEffect(() => {

        setCurrentPage(1);

    }, [search]);


    /* =====================================================
       SUMMARY
    ===================================================== */

    const totalTickets =
        income.length;


    const activeTickets =
        income.filter(
            item =>
                Number(item.status) === 1
        ).length;


    const solvedTickets =
        income.filter(
            item =>
                Number(item.status) !== 1
        ).length;


    /* =====================================================
       PAGINATION
    ===================================================== */

    const totalPages =
        Math.max(
            1,
            Math.ceil(
                filteredTickets.length /
                PER_PAGE
            )
        );


    useEffect(() => {

        if (
            currentPage >
            totalPages
        ) {

            setCurrentPage(
                totalPages
            );
        }

    }, [
        totalPages,
        currentPage
    ]);


    const currentTickets =
        useMemo(() => {

            const start =
                (
                    currentPage -
                    1
                ) *
                PER_PAGE;


            return filteredTickets.slice(
                start,
                start + PER_PAGE
            );

        }, [
            filteredTickets,
            currentPage
        ]);


    const pageNumbers =
        useMemo(() => {

            let start =
                Math.max(
                    1,
                    currentPage - 2
                );


            let end =
                Math.min(
                    totalPages,
                    currentPage + 2
                );


            if (
                end - start < 4
            ) {

                if (
                    start === 1
                ) {

                    end =
                        Math.min(
                            totalPages,
                            5
                        );

                } else {

                    start =
                        Math.max(
                            1,
                            totalPages - 4
                        );
                }
            }


            const pages = [];


            for (
                let i = start;
                i <= end;
                i++
            ) {

                pages.push(i);
            }


            return pages;

        }, [
            currentPage,
            totalPages
        ]);


    const goToPage =
        page => {

            if (
                page < 1 ||
                page > totalPages
            ) {
                return;
            }

            setCurrentPage(page);
        };


    const showingFrom =
        filteredTickets.length === 0
            ? 0
            : (
                (
                    currentPage -
                    1
                ) *
                PER_PAGE
            ) + 1;


    const showingTo =
        Math.min(
            currentPage *
            PER_PAGE,
            filteredTickets.length
        );


    /* =====================================================
       RENDER
    ===================================================== */

    return (

        <Fragment>


            {/* =================================================
                CSS
            ================================================= */}

            <style>{`

                * {
                    box-sizing: border-box;
                }


                .support-ticket-page {

                    --primary: #5b4df7;
                    --primary-dark: #4939e8;
                    --primary-soft: #f1efff;

                    --success: #12b76a;
                    --success-dark: #067647;
                    --success-soft: #ecfdf3;

                    --warning: #f79009;
                    --warning-dark: #b54708;
                    --warning-soft: #fff7ed;

                    --danger: #f04438;
                    --danger-soft: #fef3f2;

                    --blue: #1570ef;
                    --blue-soft: #eff8ff;

                    --text: #101828;
                    --text-secondary: #344054;
                    --muted: #667085;

                    --border: #e4e7ec;
                    --light: #f9fafb;

                    width: 100%;

                    padding: 20px;

                    border-radius: 20px;

                    background: #ffffff;

                    color: var(--text);

                    font-family:
                        Inter,
                        -apple-system,
                        BlinkMacSystemFont,
                        "Segoe UI",
                        Arial,
                        sans-serif;
                }


                .support-ticket-container {

                    width: 100%;

                    max-width: 1450px;

                    margin: 0 auto;
                }


                /* =============================================
                   PAGE HEADER
                ============================================= */

                .ticket-page-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    margin-bottom: 22px;
                }


                .ticket-page-title {

                    display: flex;

                    align-items: center;

                    gap: 14px;
                }


                .ticket-page-icon {

                    width: 52px;
                    height: 52px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 15px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .ticket-page-title h2 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 27px;

                    line-height: 1.25;

                    font-weight: 800;
                }


                .ticket-page-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 13px;
                }


                .ticket-create-btn {

                    min-height: 46px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 8px;

                    padding:
                        0 18px;

                    border-radius: 11px;

                    background:
                        var(--primary);

                    color: #ffffff !important;

                    font-size: 13px;

                    font-weight: 800;

                    text-decoration: none !important;

                    box-shadow:
                        0 7px 18px
                        rgba(
                            91,
                            77,
                            247,
                            .18
                        );

                    transition:
                        .18s ease;
                }


                .ticket-create-btn:hover {

                    background:
                        var(--primary-dark);

                    transform:
                        translateY(-1px);

                    color: #ffffff;
                }


                /* =============================================
                   SUMMARY CARDS
                ============================================= */

                .ticket-summary-grid {

                    display: grid;

                    grid-template-columns:
                        repeat(
                            3,
                            minmax(0, 1fr)
                        );

                    gap: 14px;

                    margin-bottom: 18px;
                }


                .ticket-summary-card {

                    padding: 18px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 15px;

                    background: #ffffff;

                    box-shadow:
                        0 5px 20px
                        rgba(
                            16,
                            24,
                            40,
                            .04
                        );
                }


                .ticket-summary-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 12px;

                    margin-bottom: 12px;
                }


                .ticket-summary-label {

                    color:
                        var(--muted);

                    font-size: 12px;

                    font-weight: 700;
                }


                .ticket-summary-icon {

                    width: 40px;
                    height: 40px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    border-radius: 11px;

                    font-size: 16px;
                }


                .ticket-summary-icon.total {

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .ticket-summary-icon.active {

                    background:
                        var(--success-soft);

                    color:
                        var(--success-dark);
                }


                .ticket-summary-icon.solved {

                    background:
                        var(--blue-soft);

                    color:
                        var(--blue);
                }


                .ticket-summary-value {

                    margin: 0;

                    color:
                        var(--text);

                    font-size: 25px;

                    line-height: 1;

                    font-weight: 800;
                }


                /* =============================================
                   MAIN CARD
                ============================================= */

                .ticket-card {

                    overflow: hidden;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 17px;

                    background: #ffffff;

                    box-shadow:
                        0 6px 24px
                        rgba(
                            16,
                            24,
                            40,
                            .045
                        );
                }


                .ticket-card-header {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 20px;

                    padding:
                        19px 20px;

                    border-bottom:
                        1px solid
                        #eef0f4;
                }


                .ticket-card-title h3 {

                    margin:
                        0 0 4px;

                    color:
                        var(--text);

                    font-size: 17px;

                    font-weight: 800;
                }


                .ticket-card-title p {

                    margin: 0;

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                /* =============================================
                   SEARCH
                ============================================= */

                .ticket-search {

                    position: relative;

                    width: 320px;

                    max-width: 100%;
                }


                .ticket-search-icon {

                    position: absolute;

                    top: 50%;
                    left: 14px;

                    transform:
                        translateY(-50%);

                    color:
                        #98a2b3;

                    font-size: 13px;

                    pointer-events: none;
                }


                .ticket-search input {

                    width: 100%;
                    height: 44px;

                    padding:
                        0 14px
                        0 41px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 10px;

                    outline: none;

                    background:
                        var(--light);

                    color:
                        var(--text);

                    font-size: 13px;

                    transition:
                        .18s ease;
                }


                .ticket-search input::placeholder {

                    color:
                        #98a2b3;
                }


                .ticket-search input:focus {

                    border-color:
                        var(--primary);

                    background: #ffffff;

                    box-shadow:
                        0 0 0 4px
                        rgba(
                            91,
                            77,
                            247,
                            .08
                        );
                }


                /* =============================================
                   TABLE
                ============================================= */

                .ticket-table-wrapper {

                    width: 100%;

                    overflow-x: auto;
                }


                .ticket-table {

                    width: 100%;

                    min-width: 850px;

                    border-collapse:
                        collapse;
                }


                .ticket-table thead {

                    background:
                        #f9fafb;
                }


                .ticket-table th {

                    padding:
                        14px 17px;

                    border-bottom:
                        1px solid
                        var(--border);

                    color:
                        var(--muted);

                    font-size: 11px;

                    font-weight: 800;

                    text-align: left;

                    text-transform:
                        uppercase;

                    letter-spacing:
                        .35px;

                    white-space: nowrap;
                }


                .ticket-table td {

                    padding:
                        16px 17px;

                    border-bottom:
                        1px solid
                        #f0f1f4;

                    color:
                        var(--text-secondary);

                    font-size: 13px;

                    vertical-align:
                        middle;
                }


                .ticket-table tbody tr {

                    transition:
                        background .15s ease;
                }


                .ticket-table tbody tr:hover {

                    background:
                        #fafaff;
                }


                .ticket-table tbody tr:last-child td {

                    border-bottom: 0;
                }


                /* =============================================
                   TICKET ID
                ============================================= */

                .ticket-id {

                    display: inline-flex;

                    align-items: center;

                    gap: 7px;

                    padding:
                        6px 9px;

                    border-radius: 8px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 12px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .ticket-id i {

                    font-size: 10px;
                }


                /* =============================================
                   SUBJECT
                ============================================= */

                .ticket-subject {

                    display: flex;

                    align-items: center;

                    gap: 10px;
                }


                .ticket-subject-icon {

                    width: 36px;
                    height: 36px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 10px;

                    background:
                        #f2f4f7;

                    color:
                        var(--text-secondary);

                    font-size: 13px;
                }


                .ticket-subject-text {

                    max-width: 350px;

                    overflow: hidden;

                    color:
                        var(--text);

                    font-size: 13px;

                    font-weight: 700;

                    text-overflow:
                        ellipsis;

                    white-space: nowrap;
                }


                /* =============================================
                   STATUS
                ============================================= */

                .ticket-status {

                    display: inline-flex;

                    align-items: center;

                    gap: 6px;

                    padding:
                        6px 10px;

                    border-radius: 50px;

                    font-size: 11px;

                    font-weight: 800;

                    white-space: nowrap;
                }


                .ticket-status-dot {

                    width: 6px;
                    height: 6px;

                    border-radius: 50%;
                }


                .ticket-status.active {

                    background:
                        var(--success-soft);

                    color:
                        var(--success-dark);
                }


                .ticket-status.active
                .ticket-status-dot {

                    background:
                        var(--success);
                }


                .ticket-status.solved {

                    background:
                        var(--blue-soft);

                    color:
                        #175cd3;
                }


                .ticket-status.solved
                .ticket-status-dot {

                    background:
                        var(--blue);
                }


                /* =============================================
                   DATE
                ============================================= */

                .ticket-date {

                    display: flex;

                    align-items: center;

                    gap: 9px;

                    white-space: nowrap;
                }


                .ticket-date-icon {

                    width: 34px;
                    height: 34px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    flex-shrink: 0;

                    border-radius: 9px;

                    background:
                        #f2f4f7;

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .ticket-date strong {

                    display: block;

                    margin-bottom: 2px;

                    color:
                        var(--text-secondary);

                    font-size: 12px;
                }


                .ticket-date span {

                    display: block;

                    color:
                        var(--muted);

                    font-size: 10px;
                }


                /* =============================================
                   VIEW BUTTON
                ============================================= */

                .ticket-view-button {

                    min-height: 37px;

                    display: inline-flex;

                    align-items: center;

                    justify-content: center;

                    gap: 7px;

                    padding:
                        0 13px;

                    border:
                        1px solid
                        #d9d6fe;

                    border-radius: 9px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary) !important;

                    font-size: 11px;

                    font-weight: 800;

                    text-decoration:
                        none !important;

                    white-space: nowrap;

                    transition:
                        .16s ease;
                }


                .ticket-view-button:hover {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color:
                        #ffffff !important;
                }


                /* =============================================
                   MESSAGE
                ============================================= */

                .ticket-message {

                    padding:
                        65px 20px;

                    text-align: center;
                }


                .ticket-message-icon {

                    width: 58px;
                    height: 58px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    margin:
                        0 auto 14px;

                    border-radius: 16px;

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);

                    font-size: 21px;
                }


                .ticket-message h4 {

                    margin:
                        0 0 6px;

                    color:
                        var(--text);

                    font-size: 16px;

                    font-weight: 800;
                }


                .ticket-message p {

                    margin:
                        0 auto 17px;

                    max-width: 400px;

                    color:
                        var(--muted);

                    font-size: 12px;

                    line-height: 1.6;
                }


                .ticket-spinner {

                    width: 39px;
                    height: 39px;

                    margin:
                        0 auto 14px;

                    border:
                        4px solid
                        #ececf2;

                    border-top-color:
                        var(--primary);

                    border-radius: 50%;

                    animation:
                        ticketSpin
                        .8s linear
                        infinite;
                }


                @keyframes ticketSpin {

                    to {

                        transform:
                            rotate(360deg);
                    }
                }


                .ticket-retry-button {

                    min-height: 39px;

                    padding:
                        0 16px;

                    border: 0;

                    border-radius: 9px;

                    background:
                        var(--primary);

                    color: #ffffff;

                    font-size: 12px;

                    font-weight: 800;

                    cursor: pointer;
                }


                /* =============================================
                   TABLE FOOTER
                ============================================= */

                .ticket-table-footer {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 15px;

                    padding:
                        15px 20px;

                    border-top:
                        1px solid
                        #eef0f4;

                    background:
                        #ffffff;
                }


                .ticket-showing {

                    color:
                        var(--muted);

                    font-size: 12px;
                }


                .ticket-showing strong {

                    color:
                        var(--text-secondary);
                }


                .ticket-pagination {

                    display: flex;

                    align-items: center;

                    gap: 6px;
                }


                .ticket-page-btn {

                    min-width: 37px;
                    height: 37px;

                    display: flex;

                    align-items: center;

                    justify-content: center;

                    padding:
                        0 10px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 9px;

                    outline: none;

                    background:
                        #ffffff;

                    color:
                        var(--text-secondary);

                    font-size: 12px;

                    font-weight: 700;

                    cursor: pointer;

                    transition:
                        .15s ease;
                }


                .ticket-page-btn:hover:not(:disabled) {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary-soft);

                    color:
                        var(--primary);
                }


                .ticket-page-btn.active {

                    border-color:
                        var(--primary);

                    background:
                        var(--primary);

                    color: #ffffff;
                }


                .ticket-page-btn:disabled {

                    opacity: .4;

                    cursor: not-allowed;
                }


                /* =============================================
                   MOBILE CARDS
                ============================================= */

                .ticket-mobile-list {

                    display: none;
                }


                .ticket-mobile-card {

                    padding: 15px;

                    margin-bottom: 11px;

                    border:
                        1px solid
                        var(--border);

                    border-radius: 13px;

                    background:
                        #ffffff;
                }


                .ticket-mobile-card:last-child {

                    margin-bottom: 0;
                }


                .ticket-mobile-top {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 10px;

                    padding-bottom: 12px;

                    margin-bottom: 12px;

                    border-bottom:
                        1px solid
                        #f0f1f4;
                }


                .ticket-mobile-subject {

                    margin-bottom: 14px;
                }


                .ticket-mobile-subject small {

                    display: block;

                    margin-bottom: 4px;

                    color:
                        var(--muted);

                    font-size: 10px;

                    font-weight: 700;

                    text-transform:
                        uppercase;
                }


                .ticket-mobile-subject strong {

                    display: block;

                    color:
                        var(--text);

                    font-size: 13px;

                    line-height: 1.5;
                }


                .ticket-mobile-bottom {

                    display: flex;

                    align-items: center;

                    justify-content:
                        space-between;

                    gap: 12px;
                }


                .ticket-mobile-date {

                    color:
                        var(--muted);

                    font-size: 11px;
                }


                /* =============================================
                   RESPONSIVE
                ============================================= */

                @media (
                    max-width: 900px
                ) {

                    .ticket-summary-grid {

                        grid-template-columns:
                            repeat(
                                3,
                                minmax(0,1fr)
                            );
                    }

                }


                @media (
                    max-width: 767px
                ) {

                    .support-ticket-page {

                        padding: 14px;

                        border-radius: 14px;
                    }


                    .ticket-page-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .ticket-page-title {

                        align-items:
                            flex-start;
                    }


                    .ticket-page-icon {

                        width: 45px;
                        height: 45px;

                        border-radius: 12px;
                    }


                    .ticket-page-title h2 {

                        font-size: 22px;
                    }


                    .ticket-create-btn {

                        width: 100%;
                    }


                    .ticket-summary-grid {

                        grid-template-columns:
                            1fr;
                    }


                    .ticket-card-header {

                        align-items:
                            stretch;

                        flex-direction:
                            column;
                    }


                    .ticket-search {

                        width: 100%;
                    }


                    .ticket-desktop-table {

                        display: none;
                    }


                    .ticket-mobile-list {

                        display: block;

                        padding: 13px;

                        background:
                            #f9fafb;
                    }


                    .ticket-table-footer {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .ticket-pagination {

                        width: 100%;

                        flex-wrap: wrap;

                        justify-content:
                            center;
                    }

                }


                @media (
                    max-width: 420px
                ) {

                    .support-ticket-page {

                        padding: 10px;
                    }


                    .ticket-mobile-bottom {

                        align-items:
                            flex-start;

                        flex-direction:
                            column;
                    }


                    .ticket-view-button {

                        width: 100%;
                    }

                }

            `}</style>


            {/* =================================================
                PAGE
            ================================================= */}

            <main
                className="support-ticket-page"
            >

                <div
                    className="support-ticket-container"
                >


                    {/* =========================================
                        HEADER
                    ========================================= */}

                    <div
                        className="ticket-page-header"
                    >

                        <div
                            className="ticket-page-title"
                        >

                            <div
                                className="ticket-page-icon"
                            >
                                <i
                                    className="fa fa-headphones"
                                    aria-hidden="true"
                                ></i>
                            </div>


                            <div>

                                <h2>
                                    Support Tickets
                                </h2>

                                <p>
                                    Create, view and manage your
                                    support requests.
                                </p>

                            </div>

                        </div>


                        <Link
                            to="/create-ticket"
                            className="ticket-create-btn"
                        >

                            <i
                                className="fa fa-plus"
                                aria-hidden="true"
                            ></i>

                            Create New Ticket

                        </Link>

                    </div>


                    {/* =========================================
                        SUMMARY
                    ========================================= */}

                    {!loading && !error && (

                        <div
                            className="ticket-summary-grid"
                        >


                            {/* TOTAL */}

                            <div
                                className="ticket-summary-card"
                            >

                                <div
                                    className="ticket-summary-top"
                                >

                                    <span
                                        className="ticket-summary-label"
                                    >
                                        Total Tickets
                                    </span>


                                    <div
                                        className="ticket-summary-icon total"
                                    >
                                        <i
                                            className="fa fa-ticket"
                                            aria-hidden="true"
                                        ></i>
                                    </div>

                                </div>


                                <h3
                                    className="ticket-summary-value"
                                >
                                    {totalTickets}
                                </h3>

                            </div>


                            {/* ACTIVE */}

                            <div
                                className="ticket-summary-card"
                            >

                                <div
                                    className="ticket-summary-top"
                                >

                                    <span
                                        className="ticket-summary-label"
                                    >
                                        Active Tickets
                                    </span>


                                    <div
                                        className="ticket-summary-icon active"
                                    >
                                        <i
                                            className="fa fa-comments"
                                            aria-hidden="true"
                                        ></i>
                                    </div>

                                </div>


                                <h3
                                    className="ticket-summary-value"
                                >
                                    {activeTickets}
                                </h3>

                            </div>


                            {/* SOLVED */}

                            <div
                                className="ticket-summary-card"
                            >

                                <div
                                    className="ticket-summary-top"
                                >

                                    <span
                                        className="ticket-summary-label"
                                    >
                                        Solved Tickets
                                    </span>


                                    <div
                                        className="ticket-summary-icon solved"
                                    >
                                        <i
                                            className="fa fa-check"
                                            aria-hidden="true"
                                        ></i>
                                    </div>

                                </div>


                                <h3
                                    className="ticket-summary-value"
                                >
                                    {solvedTickets}
                                </h3>

                            </div>

                        </div>

                    )}


                    {/* =========================================
                        TICKET LIST
                    ========================================= */}

                    <div
                        className="ticket-card"
                    >


                        {/* HEADER */}

                        <div
                            className="ticket-card-header"
                        >

                            <div
                                className="ticket-card-title"
                            >

                                <h3>
                                    Ticket History
                                </h3>

                                <p>
                                    All of your support requests
                                    are listed below.
                                </p>

                            </div>


                            {!loading &&
                                !error &&
                                income.length > 0 && (

                                <div
                                    className="ticket-search"
                                >

                                    <span
                                        className="ticket-search-icon"
                                    >
                                        <i
                                            className="fa fa-search"
                                            aria-hidden="true"
                                        ></i>
                                    </span>


                                    <input
                                        type="text"
                                        value={
                                            search
                                        }
                                        placeholder="Search ticket or subject..."
                                        onChange={
                                            e =>
                                                setSearch(
                                                    e.target.value
                                                )
                                        }
                                    />

                                </div>
                            )}

                        </div>


                        {/* =====================================
                            LOADING
                        ===================================== */}

                        {loading ? (

                            <div
                                className="ticket-message"
                            >

                                <div
                                    className="ticket-spinner"
                                ></div>

                                <h4>
                                    Loading tickets...
                                </h4>

                                <p>
                                    Please wait while we load
                                    your support ticket history.
                                </p>

                            </div>

                        ) : error ? (


                            /* =================================
                               ERROR
                            ================================= */

                            <div
                                className="ticket-message"
                            >

                                <div
                                    className="ticket-message-icon"
                                >
                                    <i
                                        className="fa fa-exclamation-triangle"
                                        aria-hidden="true"
                                    ></i>
                                </div>

                                <h4>
                                    Unable to load tickets
                                </h4>

                                <p>
                                    {error}
                                </p>

                                <button
                                    type="button"
                                    className="ticket-retry-button"
                                    onClick={
                                        fetchTickets
                                    }
                                >
                                    Try Again
                                </button>

                            </div>

                        ) : currentTickets.length ===
                            0 ? (


                            /* =================================
                               EMPTY
                            ================================= */

                            <div
                                className="ticket-message"
                            >

                                <div
                                    className="ticket-message-icon"
                                >
                                    <i
                                        className="fa fa-ticket"
                                        aria-hidden="true"
                                    ></i>
                                </div>


                                <h4>
                                    {search
                                        ? "No tickets found"
                                        : "No support tickets yet"
                                    }
                                </h4>


                                <p>

                                    {search
                                        ? "We couldn't find any ticket matching your search."
                                        : "You have not created any support tickets yet. Create a ticket if you need assistance."
                                    }

                                </p>


                                {!search && (

                                    <Link
                                        to="/create-ticket"
                                        className="ticket-create-btn"
                                    >

                                        <i
                                            className="fa fa-plus"
                                            aria-hidden="true"
                                        ></i>

                                        Create Ticket

                                    </Link>

                                )}

                            </div>

                        ) : (

                            <Fragment>


                                {/* =================================
                                    DESKTOP TABLE
                                ================================= */}

                                <div
                                    className="ticket-desktop-table"
                                >

                                    <div
                                        className="ticket-table-wrapper"
                                    >

                                        <table
                                            className="ticket-table"
                                        >

                                            <thead>

                                                <tr>

                                                    <th>
                                                        Ticket ID
                                                    </th>

                                                    <th>
                                                        Subject
                                                    </th>

                                                    <th>
                                                        Status
                                                    </th>

                                                    <th>
                                                        Created Date
                                                    </th>

                                                    <th
                                                        style={{
                                                            textAlign:
                                                                "right"
                                                        }}
                                                    >
                                                        Action
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {currentTickets.map(
                                                    (
                                                        ticket,
                                                        index
                                                    ) => {

                                                        const isActive =
                                                            Number(
                                                                ticket.status
                                                            ) === 1;


                                                        return (

                                                            <tr
                                                                key={
                                                                    ticket.id ||
                                                                    ticket.ticket ||
                                                                    index
                                                                }
                                                            >


                                                                {/* TICKET ID */}

                                                                <td>

                                                                    <span
                                                                        className="ticket-id"
                                                                    >

                                                                        <i
                                                                            className="fa fa-ticket"
                                                                            aria-hidden="true"
                                                                        ></i>

                                                                        #
                                                                        {
                                                                            ticket.ticket
                                                                        }

                                                                    </span>

                                                                </td>


                                                                {/* SUBJECT */}

                                                                <td>

                                                                    <div
                                                                        className="ticket-subject"
                                                                    >

                                                                        <div
                                                                            className="ticket-subject-icon"
                                                                        >
                                                                            <i
                                                                                className="fa fa-comment"
                                                                                aria-hidden="true"
                                                                            ></i>
                                                                        </div>


                                                                        <span
                                                                            className="ticket-subject-text"
                                                                            title={
                                                                                ticket.subject
                                                                            }
                                                                        >
                                                                            {
                                                                                ticket.subject ||
                                                                                "No Subject"
                                                                            }
                                                                        </span>

                                                                    </div>

                                                                </td>


                                                                {/* STATUS */}

                                                                <td>

                                                                    <span
                                                                        className={
                                                                            `ticket-status ${
                                                                                isActive
                                                                                    ? "active"
                                                                                    : "solved"
                                                                            }`
                                                                        }
                                                                    >

                                                                        <span
                                                                            className="ticket-status-dot"
                                                                        ></span>

                                                                        {isActive
                                                                            ? "Active"
                                                                            : "Solved"
                                                                        }

                                                                    </span>

                                                                </td>


                                                                {/* DATE */}

                                                                <td>

                                                                    <div
                                                                        className="ticket-date"
                                                                    >

                                                                        <div
                                                                            className="ticket-date-icon"
                                                                        >
                                                                            <i
                                                                                className="fa fa-calendar"
                                                                                aria-hidden="true"
                                                                            ></i>
                                                                        </div>


                                                                        <div>

                                                                            <strong>
                                                                                {formatDate(
                                                                                    ticket.created_at
                                                                                )}
                                                                            </strong>

                                                                            {formatTime(
                                                                                ticket.created_at
                                                                            ) && (

                                                                                <span>
                                                                                    {formatTime(
                                                                                        ticket.created_at
                                                                                    )}
                                                                                </span>

                                                                            )}

                                                                        </div>

                                                                    </div>

                                                                </td>


                                                                {/* ACTION */}

                                                                <td
                                                                    style={{
                                                                        textAlign:
                                                                            "right"
                                                                    }}
                                                                >

                                                                    <Link
                                                                        to={
                                                                            `/view-ticket/${ticket.ticket}`
                                                                        }
                                                                        className="ticket-view-button"
                                                                    >

                                                                        View Ticket

                                                                        <i
                                                                            className="fa fa-angle-right"
                                                                            aria-hidden="true"
                                                                        ></i>

                                                                    </Link>

                                                                </td>

                                                            </tr>

                                                        );
                                                    }
                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>


                                {/* =================================
                                    MOBILE
                                ================================= */}

                                <div
                                    className="ticket-mobile-list"
                                >

                                    {currentTickets.map(
                                        (
                                            ticket,
                                            index
                                        ) => {

                                            const isActive =
                                                Number(
                                                    ticket.status
                                                ) === 1;


                                            return (

                                                <div
                                                    className="ticket-mobile-card"
                                                    key={
                                                        ticket.id ||
                                                        ticket.ticket ||
                                                        index
                                                    }
                                                >

                                                    <div
                                                        className="ticket-mobile-top"
                                                    >

                                                        <span
                                                            className="ticket-id"
                                                        >

                                                            <i
                                                                className="fa fa-ticket"
                                                                aria-hidden="true"
                                                            ></i>

                                                            #
                                                            {
                                                                ticket.ticket
                                                            }

                                                        </span>


                                                        <span
                                                            className={
                                                                `ticket-status ${
                                                                    isActive
                                                                        ? "active"
                                                                        : "solved"
                                                                }`
                                                            }
                                                        >

                                                            <span
                                                                className="ticket-status-dot"
                                                            ></span>

                                                            {isActive
                                                                ? "Active"
                                                                : "Solved"
                                                            }

                                                        </span>

                                                    </div>


                                                    <div
                                                        className="ticket-mobile-subject"
                                                    >

                                                        <small>
                                                            Subject
                                                        </small>

                                                        <strong>
                                                            {
                                                                ticket.subject ||
                                                                "No Subject"
                                                            }
                                                        </strong>

                                                    </div>


                                                    <div
                                                        className="ticket-mobile-bottom"
                                                    >

                                                        <div
                                                            className="ticket-mobile-date"
                                                        >

                                                            <i
                                                                className="fa fa-calendar"
                                                                aria-hidden="true"
                                                            ></i>

                                                            {" "}

                                                            {formatDate(
                                                                ticket.created_at
                                                            )}

                                                        </div>


                                                        <Link
                                                            to={
                                                                `/view-ticket/${ticket.ticket}`
                                                            }
                                                            className="ticket-view-button"
                                                        >

                                                            View Ticket

                                                            <i
                                                                className="fa fa-angle-right"
                                                                aria-hidden="true"
                                                            ></i>

                                                        </Link>

                                                    </div>

                                                </div>

                                            );
                                        }
                                    )}

                                </div>

                            </Fragment>
                        )}


                        {/* =====================================
                            PAGINATION
                        ===================================== */}

                        {!loading &&
                            !error &&
                            filteredTickets.length >
                                0 && (

                            <div
                                className="ticket-table-footer"
                            >

                                <div
                                    className="ticket-showing"
                                >

                                    Showing{" "}

                                    <strong>
                                        {showingFrom}
                                    </strong>

                                    {" "}to{" "}

                                    <strong>
                                        {showingTo}
                                    </strong>

                                    {" "}of{" "}

                                    <strong>
                                        {
                                            filteredTickets.length
                                        }
                                    </strong>

                                    {" "}tickets

                                </div>


                                <div
                                    className="ticket-pagination"
                                >


                                    {/* FIRST */}

                                    <button
                                        type="button"
                                        className="ticket-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            goToPage(1)
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-double-left"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* PREVIOUS */}

                                    <button
                                        type="button"
                                        className="ticket-page-btn"
                                        disabled={
                                            currentPage === 1
                                        }
                                        onClick={() =>
                                            goToPage(
                                                currentPage -
                                                1
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-left"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* PAGE NUMBERS */}

                                    {pageNumbers.map(
                                        page => (

                                            <button
                                                type="button"
                                                key={
                                                    page
                                                }
                                                className={
                                                    `ticket-page-btn ${
                                                        currentPage ===
                                                        page
                                                            ? "active"
                                                            : ""
                                                    }`
                                                }
                                                onClick={() =>
                                                    goToPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>

                                        )
                                    )}


                                    {/* NEXT */}

                                    <button
                                        type="button"
                                        className="ticket-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            goToPage(
                                                currentPage +
                                                1
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-right"
                                            aria-hidden="true"
                                        ></i>
                                    </button>


                                    {/* LAST */}

                                    <button
                                        type="button"
                                        className="ticket-page-btn"
                                        disabled={
                                            currentPage ===
                                            totalPages
                                        }
                                        onClick={() =>
                                            goToPage(
                                                totalPages
                                            )
                                        }
                                    >
                                        <i
                                            className="fa fa-angle-double-right"
                                            aria-hidden="true"
                                        ></i>
                                    </button>

                                </div>

                            </div>

                        )}

                    </div>

                </div>

            </main>

        </Fragment>
    );
};


export default BasicDatatable;